/**
 * Unit / tower schema (DATA_SCHEMAS §3). Units are the data-driven CORE roster —
 * the masterable spine of the build. Each has in-battle upgrade `tiers` (Gold),
 * a `family` (which hero masteries & relic affixes scale), and per-family extras
 * (melee `blockSpan`, support `aura`/`income`).
 *
 * Forward-compat (kept optional for M2, fleshed out in M3/M4): `flavoredUpgrades`
 * are realm-Favor-unlocked alternate upgrade paths — the primary way each Mythic
 * Pantheon realm expresses itself on the core roster.
 */
import { z } from 'zod';
import {
  SlotTypeSchema,
  StatBlockSchema,
  StatusEffectSchema,
  TargetModeSchema,
  UnitFamilySchema,
} from './common';

/** One upgrade tier: index 0 = deploy cost+stats; later = in-battle upgrades. */
export const UnitTierSchema = z.object({
  goldCost: z.number().nonnegative(),
  /** Sparse override that merges onto the previous tier (see Stats.mergeStats). */
  stats: StatBlockSchema,
  perks: z.array(z.string()).default([]),
  onHitStatus: z.array(StatusEffectSchema).default([]),
  /** Melee-only: tier override for per-soldier hold capacity (carries the
   *  unit-level value forward when omitted). How many enemies ONE soldier body
   *  physically stops at once (a lone champion can hold several; a footman
   *  soldier holds one). Total squad capacity = squadSize × this. */
  blockCount: z.number().int().positive().optional(),
  /** Melee-only: tier override for how many soldier bodies the squad fields
   *  (carries the unit-level value forward when omitted). Mirrors the ranged
   *  `multiShot` breakpoint — an upgrade adds another body to the line. */
  squadSize: z.number().int().positive().optional(),
  /** Support-only: passive gold generation. */
  income: z.object({ goldPerS: z.number().nonnegative() }).optional(),
  /** Support-only: aura radius + flat/pct stat mods applied to nearby allies. */
  aura: z
    .object({
      radius: z.number().positive(),
      mods: z.array(
        z.object({
          stat: z.string(),
          addFlat: z.number().optional(),
          addPct: z.number().optional(),
        }),
      ),
    })
    .optional(),
});
export type UnitTier = z.infer<typeof UnitTierSchema>;

/**
 * A branching in-battle specialization. After a unit's shared base `tiers`, the
 * player picks ONE branch (e.g. Footmen → Shieldwall or Reaver); the branch's
 * `tiers` then continue the upgrade chain, merging onto the base like any tier.
 * Keeps the roster small while making the in-battle upgrade a real choice.
 */
export const UpgradeBranchSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Optional one-line flavor for the picker. */
  desc: z.string().optional(),
  /** Upgrade tiers along this path (index 0 = the first upgrade after the base). */
  tiers: z.array(UnitTierSchema).min(1),
});
export type UpgradeBranch = z.infer<typeof UpgradeBranchSchema>;

/** A realm-Favor-gated alternate upgrade path grafted onto a core unit (M4). */
export const FlavoredUpgradeSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Realm whose Favor unlocks this path (e.g. "olympus"). */
  realm: z.string(),
  /** Tier index this replaces/branches from. */
  fromTier: z.number().int().nonnegative(),
  tier: UnitTierSchema,
});

export const UnitSchema = z.object({
  id: z.string(),
  name: z.string(),
  family: UnitFamilySchema,
  validSlotTypes: z.array(SlotTypeSchema).min(1),
  unlock: z.object({
    crowns: z.number().nonnegative().default(0),
    defaultUnlocked: z.boolean().default(false),
  }),
  /** Melee-only: how much path span the unit occupies / blocks. */
  blockSpan: z.number().positive().optional(),
  /** Melee-only: how many enemies ONE soldier body holds at once (overflow
   *  enemies bypass and keep marching). A per-tier `blockCount` can raise it. */
  blockCount: z.number().int().positive().default(1),
  /** Melee-only: how many soldier bodies the squad fields. The squad's hold
   *  capacity is `squadSize × blockCount`; each soldier has its own HP and
   *  falls/respawns individually. A per-tier `squadSize` adds bodies on upgrade. */
  squadSize: z.number().int().positive().default(1),
  /** True = the player's champion: a stronger, one-per-battle deployable. (M2
   *  demo treats it as a powerful stationary unit; movement/abilities/XP land in M3.) */
  isHero: z.boolean().default(false),
  /** Max simultaneous copies deployable in a battle (heroes are limited to 1). */
  deployLimit: z.number().int().positive().optional(),
  /** Shared base upgrade chain (index 0 = deploy). After the last base tier,
   *  `branches` (if any) offer a one-time specialization choice. */
  tiers: z.array(UnitTierSchema).min(1),
  /** Branching specializations chosen once, after the base tiers. Empty = the
   *  unit upgrades linearly through `tiers` only. */
  branches: z.array(UpgradeBranchSchema).default([]),
  defaultTargetMode: TargetModeSchema.default('first'),
  /** Fraction of total gold spent refunded on sell. */
  sellRefund: z.number().min(0).max(1).default(0.6),
  /** Realm-flavored alternate upgrade paths (M4; empty for M2). */
  flavoredUpgrades: z.array(FlavoredUpgradeSchema).default([]),
  /** True = a realm's single signature "trophy" unit, not a core-roster unit. */
  isTrophy: z.boolean().default(false),
});
export type Unit = z.infer<typeof UnitSchema>;

export function parseUnit(raw: unknown): Unit {
  const result = UnitSchema.safeParse(raw);
  if (!result.success) {
    throw new Error(`Invalid unit data:\n${result.error.toString()}`);
  }
  return result.data;
}
